/**
 * TASK-192: Dynamic KYC Section Builder
 *
 * Builds dynamic section structure based on extracted data.
 * Only shows sections and fields that have actual data.
 *
 * Benefits:
 * - Better UX: No empty sections for partial extractions
 * - Flexible: Works with any extraction result structure
 * - Type-safe: Uses field mapping configuration
 */

import { KYC_FIELD_MAPPING, FieldDefinition } from '../types/kyc-field-mapping';

/**
 * Dynamic section data for rendering
 */
export interface DynamicSection {
  /** Section ID */
  id: string;

  /** Display title */
  title: string;

  /** Icon name */
  icon: string;

  /** Has extracted data? */
  hasData: boolean;

  /** Number of fields with data */
  fieldCount: number;

  /** Completeness percentage (0-100) */
  completeness: number;

  /** Extracted field data */
  fields: Record<string, any>;

  /** Field definitions for this section */
  fieldDefinitions: FieldDefinition[];

  /** Is this an array section? (e.g., wealth_sources) */
  isArray: boolean;

  /** Array items if isArray is true */
  arrayItems?: any[];
}

/**
 * Check if a value is considered "empty" for UI purposes
 */
function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (typeof value === 'string' && value.toLowerCase() === 'null') return true;
  if (typeof value === 'string' && value.toLowerCase() === 'n/a') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && Object.keys(value).length === 0) return true;
  return false;
}

/**
 * Get nested value from object using dot notation path
 *
 * @example
 * getNestedValue({ a: { b: { c: 'value' } } }, 'a.b.c') // 'value'
 */
function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return undefined;

  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    current = current[key];
  }

  return current;
}

/**
 * Extract fields from section data based on field definitions
 */
function extractSectionFields(
  sectionData: any,
  fieldDefinitions: FieldDefinition[],
  _sectionId: string // Prefixed with _ to indicate intentionally unused (reserved for future logging)
): { fields: Record<string, any>; fieldCount: number } {
  const fields: Record<string, any> = {};
  let fieldCount = 0;

  for (const fieldDef of fieldDefinitions) {
    // Get field value from extracted data using extraction path
    // For top-level sections, path is like "personalInfo.firstName"
    // For array sections, path is just "firstName" (relative to array item)
    const value = getNestedValue(sectionData, fieldDef.extractionPath);

    if (!isEmpty(value)) {
      fields[fieldDef.dbColumn] = value;
      fieldCount++;
    }
  }

  return { fields, fieldCount };
}

/**
 * Calculate section completeness percentage
 */
function calculateCompleteness(
  fieldCount: number,
  requiredFieldCount: number,
  totalFieldCount: number
): number {
  if (totalFieldCount === 0) return 0;

  // Completeness is based on:
  // - 50% weight for required fields
  // - 50% weight for all fields
  const requiredWeight = requiredFieldCount > 0 ? 0.5 : 0;
  const totalWeight = 1 - requiredWeight;

  const requiredScore = requiredFieldCount > 0 ? fieldCount / requiredFieldCount : 0;
  const totalScore = fieldCount / totalFieldCount;

  return Math.round((requiredScore * requiredWeight + totalScore * totalWeight) * 100);
}

/**
 * Build dynamic sections from extracted KYC data
 *
 * @param extractedData - Raw extracted data from staging (e.g., from API)
 * @returns Array of dynamic sections with only populated fields
 *
 * @example
 * // Passport extraction (only personal info)
 * const sections = buildDynamicKYCSections({
 *   personalInfo: {
 *     firstName: "Emma",
 *     lastName: "Watson",
 *     dateOfBirth: "1990-04-15"
 *   }
 * });
 * // Returns: [{ id: "personalInfo", fieldCount: 3, ... }]
 * // Hides: contactInfo, employmentInfo, etc. (no data)
 *
 * @example
 * // Full KYC form (all sections)
 * const sections = buildDynamicKYCSections({
 *   personalInfo: { ... },
 *   contactInfo: { ... },
 *   employmentInfo: { ... },
 *   wealthSources: [{ ... }, { ... }]
 * });
 * // Returns: All sections with data
 */
export function buildDynamicKYCSections(extractedData: any): DynamicSection[] {
  if (!extractedData || typeof extractedData !== 'object') {
    console.warn('[buildDynamicKYCSections] Invalid extracted data:', extractedData);
    return [];
  }

  const dynamicSections: DynamicSection[] = [];

  console.log('[buildDynamicKYCSections] Processing extracted data:', {
    sections: Object.keys(extractedData),
    totalSections: KYC_FIELD_MAPPING.length,
  });

  for (const sectionDef of KYC_FIELD_MAPPING) {
    // Get section data from extracted data
    // Section IDs match extraction keys (e.g., "personalInfo", "wealthSources")
    const sectionData = extractedData[sectionDef.id];

    // Skip sections with no data
    if (isEmpty(sectionData)) {
      console.log(`[buildDynamicKYCSections] Skipping empty section: ${sectionDef.id}`);
      continue;
    }

    // Handle array sections (one-to-many relationships)
    if (sectionDef.isArray) {
      if (!Array.isArray(sectionData)) {
        console.warn(
          `[buildDynamicKYCSections] Section ${sectionDef.id} should be array, got:`,
          typeof sectionData
        );
        continue;
      }

      // Process each array item
      const arrayItems: any[] = [];
      let totalFieldCount = 0;

      for (const item of sectionData) {
        const { fields, fieldCount } = extractSectionFields(
          item,
          sectionDef.fields,
          sectionDef.id
        );

        if (fieldCount > 0) {
          arrayItems.push({ ...item, _extractedFields: fields });
          totalFieldCount += fieldCount;
        }
      }

      // Skip if no items have data
      if (arrayItems.length === 0) {
        console.log(`[buildDynamicKYCSections] Skipping array section with no items: ${sectionDef.id}`);
        continue;
      }

      // Calculate completeness
      const requiredFieldCount = sectionDef.fields.filter(f => f.required).length;
      const avgFieldsPerItem = totalFieldCount / arrayItems.length;
      const completeness = calculateCompleteness(
        avgFieldsPerItem,
        requiredFieldCount,
        sectionDef.fields.length
      );

      dynamicSections.push({
        id: sectionDef.id,
        title: sectionDef.title,
        icon: sectionDef.icon,
        hasData: true,
        fieldCount: totalFieldCount,
        completeness,
        fields: {}, // Not used for array sections
        fieldDefinitions: sectionDef.fields,
        isArray: true,
        arrayItems,
      });

      console.log(`[buildDynamicKYCSections] Added array section: ${sectionDef.id}`, {
        items: arrayItems.length,
        totalFields: totalFieldCount,
        completeness: `${completeness}%`,
      });
    } else {
      // Handle object sections (one-to-one relationships)
      // Pass full extractedData because extraction paths are absolute (e.g., "personalInfo.firstName")
      const { fields, fieldCount } = extractSectionFields(
        extractedData,
        sectionDef.fields,
        sectionDef.id
      );

      // Skip sections with no extracted fields
      if (fieldCount === 0) {
        console.log(`[buildDynamicKYCSections] Skipping section with no fields: ${sectionDef.id}`);
        continue;
      }

      // Calculate completeness
      const requiredFieldCount = sectionDef.fields.filter(f => f.required).length;
      const completeness = calculateCompleteness(
        fieldCount,
        requiredFieldCount,
        sectionDef.fields.length
      );

      dynamicSections.push({
        id: sectionDef.id,
        title: sectionDef.title,
        icon: sectionDef.icon,
        hasData: true,
        fieldCount,
        completeness,
        fields,
        fieldDefinitions: sectionDef.fields,
        isArray: false,
      });

      console.log(`[buildDynamicKYCSections] Added section: ${sectionDef.id}`, {
        fields: fieldCount,
        completeness: `${completeness}%`,
      });
    }
  }

  console.log(`[buildDynamicKYCSections] Built ${dynamicSections.length} dynamic sections`);

  return dynamicSections;
}

/**
 * Get overall completeness score across all sections
 */
export function getOverallCompleteness(sections: DynamicSection[]): number {
  if (sections.length === 0) return 0;

  const totalCompleteness = sections.reduce((sum, section) => sum + section.completeness, 0);
  return Math.round(totalCompleteness / sections.length);
}

/**
 * Get total field count across all sections
 */
export function getTotalFieldCount(sections: DynamicSection[]): number {
  return sections.reduce((sum, section) => sum + section.fieldCount, 0);
}

/**
 * Filter sections by completeness threshold
 *
 * @param sections - Dynamic sections
 * @param minCompleteness - Minimum completeness percentage (0-100)
 * @returns Sections above threshold
 */
export function filterByCompleteness(
  sections: DynamicSection[],
  minCompleteness: number
): DynamicSection[] {
  return sections.filter(section => section.completeness >= minCompleteness);
}
